import {ReactElement, ReactNode} from 'react';
import React from 'react';
import clsx from 'clsx';
import {useAuth} from '@common/auth/use-auth';
import {NotificationDialogTrigger} from '@common/notifications/dialog/notification-dialog-trigger';
import {Menu, MenuTrigger} from '@ui/menu/menu-trigger';
import {useCustomMenu} from '@common/menus/use-custom-menu';
import {createSvgIconFromTree} from '@ui/icons/create-svg-icon';
import {Trans} from '@ui/i18n/trans';
import {IconButton} from '@ui/buttons/icon-button';
import {Item} from '@ui/forms/listbox/item';
import {useNavigate} from '@common/ui/navigation/use-navigate';
import {useIsDarkMode} from '@ui/themes/use-is-dark-mode';
import {useSettings} from '@ui/settings/use-settings';
import {ButtonColor} from '@ui/buttons/get-shared-button-style';
import {MenuIcon} from '@ui/icons/material/Menu';
import {
  NavbarAuthUser,
  NavbarAuthUserProps,
} from '@common/ui/navigation/navbar/navbar-auth-user';
import {NavbarAuthButtons} from '@common/ui/navigation/navbar/navbar-auth-buttons';
import {useDarkThemeVariables} from '@ui/themes/use-dark-theme-variables';
import {Logo} from '@common/ui/navigation/navbar/logo';
import {useLightThemeVariables} from '@ui/themes/use-light-theme-variables';
import {isAbsoluteUrl} from '@ui/utils/urls/is-absolute-url';
import {MenuItemConfig} from '@common/menus/menu-config';
import {useIsMobileMediaQuery} from '@ui/utils/hooks/is-mobile-media-query';
import {useThemeSelector} from '@ui/themes/theme-selector-context';

type NavbarColor = 'primary' | 'bg' | 'bg-alt' | 'transparent' | string;

export interface NavbarProps {
  hideLogo?: boolean | null;
  toggleButton?: ReactElement;
  children?: ReactNode;
  className?: string;
  color?: NavbarColor;
  bgOpacity?: number | string;
  darkModeColor?: NavbarColor;
  logoColor?: 'dark' | 'light';
  textColor?: string;
  primaryButtonColor?: ButtonColor;
  border?: string;
  size?: 'xs' | 'sm' | 'md';
  rightChildren?: ReactNode;
  menuPosition?: string;
  authMenuItems?: NavbarAuthUserProps['items'];
  alwaysDarkMode?: boolean;
  wrapInContainer?: boolean;
}

export function Navbar(props: NavbarProps) {
  let {
    hideLogo,
    toggleButton,
    children,
    className,
    border,
    size = 'md',
    color,
    textColor,
    darkModeColor,
    rightChildren,
    menuPosition,
    primaryButtonColor,
    authMenuItems,
    logoColor,
    alwaysDarkMode = false,
    wrapInContainer = false,
  } = props;

  const isDarkMode = useIsDarkMode() || alwaysDarkMode;
  const isMobile = useIsMobileMediaQuery();
  const {notifications} = useSettings();
  const {isLoggedIn} = useAuth();
  const {selectedTheme} = useThemeSelector();
  const darkThemeVars = useDarkThemeVariables();
  const lightThemeVars = useLightThemeVariables();
  const showNotifButton = isLoggedIn && notifications?.integrated;

  // Get color from currently active theme's navbar color
  // This ensures the navbar color works for ALL themes (Dark, Light, AFN, custom themes, etc.)
  const activeThemeNavbarColor = selectedTheme?.values?.['--be-navbar-color'];
  
  // If no explicit color prop is passed, use the active theme's navbar color
  // IMPORTANT: Always prefer selectedTheme over isDarkMode to ensure theme-specific colors
  if (!color) {
    if (activeThemeNavbarColor) {
      // Use the currently selected theme's navbar color (works for all themes)
      color = activeThemeNavbarColor;
    } else if (selectedTheme?.is_dark) {
      // Fallback: if selected theme is dark but has no navbar color, use Dark theme default
      color = darkThemeVars?.['--be-navbar-color'] ?? 'bg-alt';
    } else if (selectedTheme && !selectedTheme.is_dark) {
      // Fallback: if selected theme is light but has no navbar color, use Light theme default
      color = lightThemeVars?.['--be-navbar-color'] ?? '207 46 46';
    } else if (isDarkMode) {
      // Final fallback: no selected theme, use system dark mode
      color = darkThemeVars?.['--be-navbar-color'] ?? 'bg-alt';
    } else {
      // Final fallback: no selected theme, use system light mode
      color = lightThemeVars?.['--be-navbar-color'] ?? '207 46 46';
    }
  }
  
  // Set darkModeColor fallback (only used if color prop is explicitly passed and darkModeColor is not)
  if (!darkModeColor && selectedTheme?.is_dark && !activeThemeNavbarColor) {
    darkModeColor = darkThemeVars?.['--be-navbar-color'] ?? 'bg-alt';
  }
  
  // Check if color is RGB value (e.g., "207 46 46") or preset (e.g., "primary", "bg", "bg-alt", "transparent")
  const isRgbColor = /^\d+\s+\d+\s+\d+$/.test(color);
  const navbarStyle = isRgbColor 
    ? { 
        backgroundColor: `rgb(${color})`,
        ...(alwaysDarkMode ? darkThemeVars : undefined)
      }
    : (alwaysDarkMode ? darkThemeVars : undefined);

  return (
    <div
      className={clsx(
        isRgbColor ? 'border-b' : getColorStyle(color, textColor),
        size === 'md' && 'h-64',
        size === 'sm' && 'h-54 py-4',
        size === 'xs' && 'h-48 py-4',
        border,
        className,
        'flex justify-center',
      )}
    >
      <div
        style={navbarStyle}
        className={clsx(
          'flex h-full items-center gap-4 md:gap-6 @container',
          'max-w-[1232px] w-full',
          wrapInContainer && 'container mx-auto',
        )}
      >
        {!hideLogo && (
          <Logo
            size="h-full max-h-26 md:max-h-36"
            className="flex-shrink-0 pl-[14px]"
            color={resolveLogoColor({
              navbarColor: color,
              logoColor,
              isDarkMode,
            })}
          />
        )}

        {toggleButton}
        
        <div className="flex-1 flex items-center justify-center gap-4 md:gap-6">
          {children}
          <MobileMenu position={menuPosition} />
          <DesktopMenu position={menuPosition} />
        </div>

        <div className="flex-shrink-0 flex items-center gap-3 md:gap-6 pr-[14px]">
          {rightChildren}
          {showNotifButton && <NotificationDialogTrigger />}
          {isLoggedIn ? (
            <NavbarAuthUser
              variant={isMobile ? 'compact' : 'wide'}
              items={authMenuItems}
            />
          ) : (
            <NavbarAuthButtons
              navbarColor={color}
              primaryButtonColor={primaryButtonColor}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/* ------------------ DESKTOP MENU WITH DROPDOWNS ------------------ */
interface DesktopMenuProps {
  position: NavbarProps['menuPosition'];
}

function DesktopMenu({position}: DesktopMenuProps) {
    const customMenu = useCustomMenu(position);

  // Guard against null / undefined menu or empty items
  if (!customMenu?.items?.length) return null;

  return (
    <nav className="relative mx-14 text-sm max-md:hidden flex gap-8">
      {customMenu.items.map((item: MenuItemConfig) => (
        <MenuItemWithDropdown key={item.id} item={item} />
      ))}
    </nav>
  );
}

function MenuItemWithDropdown({item}: {item: MenuItemConfig}) {
  const navigate = useNavigate();
  const handleClick = () => {
    if (isAbsoluteUrl(item.action)) {
      window.open(item.action, item.target ?? '_self')?.focus();
    } else if (item.action) {
      navigate(item.action);
    }
  };

  const hasChildren = Array.isArray(item.children) && item.children.length > 0;

  return (
    <div className="relative group">
      <button
        onClick={handleClick}
        className="opacity-90 hover:underline hover:opacity-100 flex items-center gap-1"
      >
        <Trans message={item.label} />
        {hasChildren && <span className="ml-1 text-xs">▾</span>}
      </button>

     {hasChildren && (
  <div className="absolute left-0 top-full hidden group-hover:block bg-paper shadow-lg rounded mt-2 min-w-[180px] z-50 border">
    {(item.children ?? []).map((child: MenuItemConfig) => {
      const childHasChildren = Array.isArray(child.children) && child.children.length > 0;
      
      return (
        <div key={child.id} className="relative group/submenu">
          <button
            onClick={() => {
              if (child.action) {
                if (isAbsoluteUrl(child.action)) {
                  window.open(child.action, child.target ?? '_self')?.focus();
                } else {
                  navigate(child.action);
                }
              }
            }}
            className="block w-full text-left px-4 py-3 hover:bg-primary/10 text-main hover:text-primary transition-colors first:rounded-t flex items-center justify-between"
          >
            <Trans message={child.label} />
            {childHasChildren && <span className="ml-2 text-xs">▸</span>}
          </button>
          
          {childHasChildren && (
            <div className="absolute left-full top-0 hidden group-hover/submenu:block bg-paper shadow-lg rounded ml-1 min-w-[180px] z-50 border">
              {(child.children ?? []).map((grandchild: MenuItemConfig) => (
                <button
                  key={grandchild.id}
                  onClick={() => {
                    if (isAbsoluteUrl(grandchild.action)) {
                      window.open(grandchild.action, grandchild.target ?? '_self')?.focus();
                    } else if (grandchild.action) {
                      navigate(grandchild.action);
                    }
                  }}
                  className="block w-full text-left px-4 py-3 hover:bg-primary/10 text-main hover:text-primary transition-colors first:rounded-t last:rounded-b text-sm"
                >
                  <Trans message={grandchild.label} />
                </button>
              ))}
            </div>
          )}
        </div>
      );
    })}
  </div>
)}

    </div>
  );
}

/* ------------------ MOBILE MENU ------------------ */
interface MobileMenuProps {
  position: NavbarProps['menuPosition'];
}

function MobileMenu({position}: MobileMenuProps) {
  const navigate = useNavigate();
  const menu = useCustomMenu(position);
  if (!menu?.items.length) return null;

  const handleItemClick = (item: MenuItemConfig) => {
    if (isAbsoluteUrl(item.action)) {
      window.open(item.action, item.target)?.focus();
    } else {
      navigate(item.action);
    }
  };

  const renderMenuItems = (items: MenuItemConfig[], level = 0): React.ReactNode[] => {
    const result: React.ReactNode[] = [];
    
    items.forEach(item => {
      const Icon = item.icon && createSvgIconFromTree(item.icon);
      const hasChildren = Array.isArray(item.children) && item.children.length > 0;
      const paddingClass = level === 0 ? 'pl-8' : level === 1 ? 'pl-16' : level === 2 ? 'pl-24' : 'pl-32';
      
      result.push(
        <Item
          key={item.id}
          value={item.action}
          onSelected={() => handleItemClick(item)}
          startIcon={Icon && <Icon />}
          className={paddingClass}
        >
          <Trans message={item.label} />
          {hasChildren && <span className="ml-2">▾</span>}
        </Item>
      );
      
      if (hasChildren && item.children) {
        result.push(...renderMenuItems(item.children, level + 1));
      }
    });
    
    return result;
  };

  return (
    <MenuTrigger>
      <IconButton className="md:hidden" aria-label="Toggle menu">
        <MenuIcon />
      </IconButton>
      <Menu>
        {renderMenuItems(menu.items)}
      </Menu>
    </MenuTrigger>
  );
}

/* ------------------ HELPERS ------------------ */
function getColorStyle(color: string, textColor?: string): string {
  switch (color) {
    case 'primary':
      return `bg-primary ${textColor || 'text-on-primary'} border-b-primary`;
    case 'bg':
      return `bg ${textColor || 'text-main'} border-b`;
    case 'bg-alt':
      return `bg-alt ${textColor || 'text-main'} border-b`;
    case 'transparent':
      return `bg-transparent ${textColor || 'text-white'}`;
    default:
      return `${color} ${textColor}`;
  }
}

function resolveLogoColor({
  logoColor,
  navbarColor,
  isDarkMode,
}: {
  logoColor?: 'light' | 'dark';
  navbarColor: NavbarColor;
  isDarkMode: boolean;
}): 'dark' | 'light' {
  if (logoColor != null) return logoColor;
  if (isDarkMode) return 'light';
  if (navbarColor === 'bg' || navbarColor === 'bg-alt') return 'dark';
  return 'light';
}
